import React from 'react';
import { isPhoneViewport } from '../../utils/mobileViewport';

interface Tab {
  id: string;
  label: string;
  icon?: string | React.ReactNode;
  onClick: () => void;
  active?: boolean;
  badge?: number;
}

interface PageTabsProps {
  tabs: Tab[];
  /**
   * Pixel offset from top — should match MobileTopBar height (52px).
   * Defaults to 52 because this component only renders on mobile where
   * the new MobileTopBar (52px) replaces the desktop header.
   */
  topOffset?: number;
  /**
   * Dark variant — used on the Vibes page.
   * Sub-tabs bar gets dark brand-green background (#1e4a40),
   * active tab: white text + #5dcaa5 underline,
   * inactive: rgba(255,255,255,0.5).
   */
  isDark?: boolean;
  /** Fade sub-tabs while scrolling feed content */
  dimmed?: boolean;
}

const BRAND       = '#2d6b5e';
const BRAND_DARK  = '#1e4a40';
const ACTIVE_CLR  = BRAND;
const INACTIVE    = 'rgba(90,88,82,0.75)';

/**
 * PageTabs — Horizontal scrollable sub-navigation strip (42px, mobile only).
 *
 * Sticks just below the MobileTopBar (top: topOffset, default 52px).
 * Uses inline styles for reliable rendering.
 */
const PageTabs: React.FC<PageTabsProps> = ({
  tabs,
  topOffset = 52,
  isDark = false,
  dimmed = false,
}) => {
  const [isMobile, setIsMobile] = React.useState(false);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const activeId = tabs.find((tab) => tab.active)?.id;

  React.useEffect(() => {
    const check = () => setIsMobile(isPhoneViewport());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  React.useEffect(() => {
    if (!isMobile || !activeId) return;
    const activeEl = scrollerRef.current?.querySelector('[data-active-tab="true"]') as HTMLElement | null;
    activeEl?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [activeId, isMobile]);

  if (!isMobile || tabs.length === 0) return null;

  const barBg       = isDark ? BRAND_DARK : '#ffffff';
  const barBorder   = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)';
  const activeColor = isDark ? '#ffffff' : ACTIVE_CLR;
  const activeUnder = isDark ? '#5dcaa5' : ACTIVE_CLR;
  const inactiveClr = isDark ? 'rgba(255,255,255,0.50)' : INACTIVE;

  return (
    <div
      ref={scrollerRef}
      style={{
        position: 'sticky',
        top: topOffset,
        zIndex: 40,
        width: '100%',
        backgroundColor: barBg,
        borderBottom: `0.5px solid ${barBorder}`,
        height: '42px',
        display: 'flex',
        alignItems: 'stretch',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        padding: '0 10px',
        scrollPaddingInline: '10px',
        WebkitOverflowScrolling: 'touch',
        flexShrink: 0,
      } as React.CSSProperties}
    >
      {tabs.map((tab) => {
        const isActive = tab.active ?? false;
        return (
          <button
            key={tab.id}
            onClick={tab.onClick}
            data-active-tab={isActive ? 'true' : undefined}
            aria-current={isActive ? 'page' : undefined}
            style={{
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              padding: '0 11px',
              whiteSpace: 'nowrap',
              fontSize: '13px',
              fontWeight: isActive ? 500 : 400,
              color: isActive ? activeColor : inactiveClr,
              background: 'none',
              border: 'none',
              borderBottom: isActive
                ? `2px solid ${activeUnder}`
                : '2px solid transparent',
              cursor: 'pointer',
              transition: 'color 0.13s, border-color 0.13s',
              opacity: dimmed ? 0.15 : 1,
              transitionProperty: 'color, border-color, opacity',
              transitionDuration: '300ms',
              transitionTimingFunction: 'ease-in-out',
              WebkitTapHighlightColor: 'transparent',
              outline: 'none',
              fontFamily: 'inherit',
            } as React.CSSProperties}
          >
            {tab.icon && (
              <span style={{ display: 'flex', alignItems: 'center', fontSize: '15px' }}>
                {tab.icon}
              </span>
            )}
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '16px',
                  height: '16px',
                  padding: '0 4px',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: 700,
                  backgroundColor: isActive ? activeColor : 'rgba(0,0,0,0.12)',
                  color: isActive ? (isDark ? BRAND_DARK : '#fff') : '#374151',
                }}
              >
                {tab.badge > 9 ? '9+' : tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PageTabs;
